import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Add as AddIcon,
  DeleteOutline,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import { VenueDocument } from "../../types/venues.types";
import { tokens } from "../../../../theme";
import AddFloorPlanModal from "./AddFloorPlans";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs`;

// ── Types ──────────────────────────────────────────────────────────────────

interface FloorPlanFile extends VenueDocument {
  venueName: string;
  venueId: number;
  isChild: boolean;
}

interface AllFloorPlansProps {
  venueData: {
    id: number;
    name: string;
    files: VenueDocument[];
    children?: {
      id: number;
      name: string;
      files: VenueDocument[];
    }[];
  };
  onRefresh?: () => void;
}

type PdfState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "blob"; url: string }
  | { status: "google"; src: string }
  | { status: "error" };

// ── Helpers ────────────────────────────────────────────────────────────────

const isPdf = (src: string): boolean =>
  !!src?.toLowerCase().endsWith(".pdf") || src?.includes("application/pdf");

const getFileName = (src: string): string => {
  try {
    const raw = decodeURIComponent(src).split("/").pop() ?? "";
    const withoutExt = raw.replace(/\.[^.]+$/, "");
    const withoutSuffix = withoutExt.replace(/_[A-Za-z0-9]{6,12}$/, "");
    return withoutSuffix.replace(/_/g, " ").trim() || "Floor Plan";
  } catch {
    return "Floor Plan";
  }
};

// ── PdfThumbnail ───────────────────────────────────────────────────────────

const PdfThumbnail: React.FC<{
  src: string;
  fileName: string;
  onClick: () => void;
}> = ({ src, fileName, onClick }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      try {
        const pdf = await pdfjsLib.getDocument({
          url: src,
          withCredentials: false,
          cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/cmaps/`,
          cMapPacked: true,
        }).promise;
        if (cancelled) return;

        const page = await pdf.getPage(1);
        if (cancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const desiredHeight = 140;
        const viewport = page.getViewport({ scale: 1 });
        const scale = desiredHeight / viewport.height;
        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        await page.render({ canvas, viewport: scaledViewport }).promise;
        if (!cancelled) setFailed(false);
      } catch (err) {
        console.warn("PDF thumbnail failed:", err);
        if (!cancelled) setFailed(true);
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <Box
      onClick={onClick}
      sx={{
        width: "100%",
        height: 140,
        borderRadius: 1,
        cursor: "pointer",
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "grey.50",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.2s ease",
        "&:hover": { transform: "scale(1.02)" },
        "&:hover .pdf-overlay": { opacity: 1 },
      }}
    >
      {failed ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            px: 1,
          }}
        >
          <PictureAsPdfIcon sx={{ fontSize: 36, color: "#C29363" }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {fileName}
          </Typography>
        </Box>
      ) : (
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}

      <Box
        className="pdf-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.45)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          opacity: 0,
          transition: "opacity 0.2s",
        }}
      >
        <PictureAsPdfIcon sx={{ fontSize: 28, color: "#fff" }} />
        <Typography
          sx={{ fontSize: "0.65rem", color: "#fff", fontWeight: 600 }}
        >
          Click to open
        </Typography>
      </Box>
    </Box>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────

const AllFloorPlans: React.FC<AllFloorPlansProps> = ({
  venueData,
  onRefresh,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [selectedFile, setSelectedFile] = useState<FloorPlanFile | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pdfState, setPdfState] = useState<PdfState>({ status: "idle" });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [filterVenueId, setFilterVenueId] = useState<number | "all">("all");
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Aggregate all floor plans: parent first, then each child room
  const allFloorPlans: FloorPlanFile[] = useMemo(
    () => [
      ...venueData.files
        .filter((f) => f.file_type === "floor_plan")
        .map((f) => ({
          ...f,
          venueName: venueData.name,
          venueId: venueData.id,
          isChild: false,
        })),
      ...(venueData.children ?? []).flatMap((child) =>
        child.files
          .filter((f) => f.file_type === "floor_plan")
          .map((f) => ({
            ...f,
            venueName: child.name,
            venueId: child.id,
            isChild: true,
          })),
      ),
    ],
    [venueData],
  );

  const filterOptions = [
    { id: venueData.id, label: `${venueData.name} (Main Venue)` },
    ...(venueData.children ?? []).map((c) => ({ id: c.id, label: c.name })),
  ];

  const [localFloorPlans, setLocalFloorPlans] =
    useState<FloorPlanFile[]>(allFloorPlans);

  useEffect(() => {
    setLocalFloorPlans(allFloorPlans);
  }, [allFloorPlans]);
  const filteredFloorPlans =
    filterVenueId === "all"
      ? localFloorPlans
      : localFloorPlans.filter((fp) => fp.venueId === filterVenueId);

  // PDF blob/fallback logic
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (!selectedFile || !isPdf(selectedFile.file)) {
      setPdfState({ status: "idle" });
      return;
    }

    setPdfState({ status: "loading" });

    fetch(selectedFile.file)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfState({ status: "blob", url: objectUrl });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn(
          "Blob fetch failed, falling back to Google Docs viewer:",
          err,
        );
        setPdfState({
          status: "google",
          src: `https://docs.google.com/viewer?url=${encodeURIComponent(selectedFile.file)}&embedded=true`,
        });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const handleOpen = (fp: FloorPlanFile) => {
    setCurrentIndex(allFloorPlans.findIndex((f) => f.id === fp.id));
    setSelectedFile(fp);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPdfState({ status: "idle" });
  };

  const handlePrev = () => {
    const newIndex =
      currentIndex > 0 ? currentIndex - 1 : allFloorPlans.length - 1;
    setCurrentIndex(newIndex);
    setSelectedFile(allFloorPlans[newIndex]);
  };

  const handleNext = () => {
    const newIndex =
      currentIndex < allFloorPlans.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setSelectedFile(allFloorPlans[newIndex]);
  };
  const handleDelete = async (e: React.MouseEvent, fileId: number) => {
    e.stopPropagation();
    setDeletingId(fileId);
    try {
      await apiService.delete(`/api/venues/document/${fileId}/`);
      setLocalFloorPlans((prev) => prev.filter((fp) => fp.id !== fileId));
      if (selectedFile?.id === fileId) handleClose();
      onRefresh?.();
      toast.success("Floor plan deleted successfully");
    } catch (err) {
      const error = err as Error;
      console.error("Failed to delete floor plan", error.message);
      toast.error("Failed to delete floor plan. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };
  const renderPdfViewer = () => {
    switch (pdfState.status) {
      case "loading":
        return (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              bgcolor: colors.primary[800],
            }}
          >
            <PictureAsPdfIcon sx={{ fontSize: 48, color: "#C29363" }} />
            <Typography variant="body2" color="text.secondary">
              Loading PDF…
            </Typography>
          </Box>
        );
      case "blob":
        return (
          <iframe
            src={pdfState.url}
            title="Floor Plan"
            width="100%"
            height="100%"
            style={{ border: "none", flex: 1 }}
          />
        );
      case "google":
        return (
          <iframe
            src={pdfState.src}
            title="Floor Plan"
            width="100%"
            height="100%"
            style={{ border: "none", flex: 1 }}
          />
        );
      default:
        return (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <PictureAsPdfIcon sx={{ fontSize: 48, color: "#C29363" }} />
            <Typography variant="body2" color="text.secondary">
              Unable to preview PDF.
            </Typography>
          </Box>
        );
    }
  };

  if (allFloorPlans.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          No floor plans available.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          sx={{
            textTransform: "none",
            borderColor: "#C29363",
            color: "#C29363",
          }}
        >
          Add Floor Plan
        </Button>
        <AddFloorPlanModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={() => {
            setAddModalOpen(false);
            onRefresh?.();
          }}
          parentVenue={{ id: venueData.id, name: venueData.name }}
          childRooms={venueData.children ?? []}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* ── Header row ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <FormControl sx={{ width: 140 }}>
          <Select
            value={filterVenueId}
            onChange={(e) => setFilterVenueId(e.target.value as number | "all")}
            displayEmpty
            size="small"
            sx={{
              height: 40,
            }}
          >
            <MenuItem value="all">All</MenuItem>
            {filterOptions.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          sx={{
            height: 40,
            width: 140,
            textTransform: "none",
            borderColor: "#C29363",
            color: "#C29363",
            fontSize: "10px",
          }}
        >
          Add Floor Plan
        </Button>
      </Box>
      <Grid container spacing={2}>
        {filteredFloorPlans.map((fp) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={fp.id}>
            <Box
              sx={{
                position: "relative",
                "&:hover .delete-btn": { opacity: 1 },
              }}
            >
              {isPdf(fp.file) ? (
                <PdfThumbnail
                  src={fp.file}
                  fileName={getFileName(fp.file)}
                  onClick={() => handleOpen(fp)}
                />
              ) : (
                <Box
                  component="img"
                  src={fp.file}
                  alt={getFileName(fp.file)}
                  onClick={() => handleOpen(fp)}
                  sx={{
                    width: "100%",
                    height: 140,
                    objectFit: "cover",
                    borderRadius: 1,
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "transform 0.2s ease",
                    "&:hover": { transform: "scale(1.02)" },
                  }}
                />
              )}

              {/* ── Delete button ── */}
              <IconButton
                className="delete-btn"
                onClick={(e) => handleDelete(e, fp.id)}
                disabled={deletingId === fp.id}
                size="small"
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  padding: "4px",
                  "&:hover": { bgcolor: "rgba(211,47,47,0.85)" },
                  "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {deletingId === fp.id ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DeleteOutline sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block", mt: 0.5, px: 0.5, fontWeight: 600 }}
            >
              {fp.venueName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block", px: 0.5 }}
            >
              {getFileName(fp.file)}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Lightbox dialog */}
      <Dialog
        open={!!selectedFile}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: colors.primary[900], borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Floor Plan
            </Typography>
            {selectedFile && (
              <Typography variant="caption" color="text.secondary">
                {selectedFile.venueName}
                {selectedFile.isChild ? " · Room" : ""}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, position: "relative" }}>
          {selectedFile && (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {allFloorPlans.length > 1 && (
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: colors.primary[900],
                    boxShadow: 2,
                    zIndex: 1,
                    "&:hover": { bgcolor: colors.primary[800] },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}

              {isPdf(selectedFile.file) ? (
                <Box
                  sx={{
                    width: "100%",
                    height: 520,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {renderPdfViewer()}
                  <Box
                    sx={{
                      p: 1,
                      textAlign: "center",
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      component="a"
                      href={selectedFile.file}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{
                        color: "#C29363",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      Open PDF in new tab ↗
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={selectedFile.file}
                  alt={getFileName(selectedFile.file)}
                  sx={{ width: "100%", maxHeight: 480, objectFit: "contain" }}
                />
              )}

              {allFloorPlans.length > 1 && (
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: colors.primary[900],
                    boxShadow: 2,
                    zIndex: 1,
                    "&:hover": { bgcolor: colors.primary[800] },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <AddFloorPlanModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          setAddModalOpen(false);
          onRefresh?.();
        }}
        parentVenue={{ id: venueData.id, name: venueData.name }}
        childRooms={venueData.children ?? []}
      />
    </Box>
  );
};

export default AllFloorPlans;
